import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  Badge,
  Banner,
  ButtonGroup,
  Card,
  CardBody,
  CardFoot,
  CardHead,
  KV,
  KVRow,
  LoadError,
  OutcomePnl,
  PageHead,
  Seg,
  SideBadge,
  Skeleton,
  Stat,
  StateBlock,
  StateTag,
} from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { gbp, odds, pnl, toKickOff } from '../lib/format.ts'
import type { PnlBet, PositionRow, PositionsPayload } from '../../shared/types.ts'

type Filter = 'all' | 'in-play' | 'pre-match'
type Sort = 'liability' | 'kickoff'

/**
 * Every position we hold, with liability and the outcome-P&L vector per
 * runner. The vector is the point: a position is not one number.
 */
export function Positions() {
  usePageTitle('Positions')
  const { data, error, loading, reload } = useApi<PositionsPayload>('/positions', 5000)
  const [filter, setFilter] = useState<Filter>('all')
  const [sort, setSort] = useState<Sort>('liability')
  const [event, setEvent] = useState('all')
  const [strategy, setStrategy] = useState('all')

  const rows = useMemo(() => {
    let list = data?.positions ?? []
    if (filter === 'in-play') list = list.filter((p) => p.inPlay)
    if (filter === 'pre-match') list = list.filter((p) => !p.inPlay)
    if (event !== 'all') list = list.filter((p) => p.event === event)
    if (strategy !== 'all') list = list.filter((p) => p.strategy === strategy)

    return [...list].sort((a, b) => {
      if (sort === 'kickoff') return (a.startTime ?? '').localeCompare(b.startTime ?? '')
      return b.liability - a.liability
    })
  }, [data, filter, sort, event, strategy])

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>

  const events = [...new Set((data?.positions ?? []).map((p) => p.event))]
  const strategies = [...new Set((data?.positions ?? []).map((p) => p.strategy))]
  const t = data?.totals

  return (
    <main className="content">
      <PageHead
        title="Open positions"
        sub="Every open bet, with what it stands to lose and what each result would mean."
        actions={
          <Link className="btn primary" to="/">
            Browse games
          </Link>
        }
      />

      {data?.orphan && <OrphanBanner orphan={data.orphan} />}

      <div className="grid cols-3" style={{ marginBottom: 16 }}>
        <Card pad>
          <Stat
            large
            label="Total liability at risk"
            value={loading ? '—' : gbp(t?.liability ?? 0)}
            sub="What your open bets stand to lose"
          />
        </Card>
        <Card pad>
          <Stat
            large
            label="Open positions"
            value={loading ? '—' : (t?.openPositions ?? 0) + (t?.openAccumulators ?? 0)}
            sub={`across ${t?.openMarkets ?? 0} market${t?.openMarkets === 1 ? '' : 's'}${
              t?.openAccumulators ? ` · ${t.openAccumulators} accumulator${t.openAccumulators === 1 ? '' : 's'}` : ''
            }`}
          />
        </Card>
        <Card pad>
          <Stat
            large
            label="Working orders"
            value={loading ? '—' : (t?.workingOrders ?? 0)}
            sub={`${gbp(t?.unmatchedSize ?? 0)} unmatched · ${t?.matchedFills ?? 0} matched`}
          />
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <CardBody style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <Seg
            label="Position filter"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All open' },
              { value: 'in-play', label: 'In-play' },
              { value: 'pre-match', label: 'Pre-match' },
            ]}
          />
          <div className="divider-v" style={{ height: 26 }} />
          <label className="lbl u-faint" style={{ fontSize: 'var(--fs-sm)' }} htmlFor="pos-event">
            Event
          </label>
          <select id="pos-event" className="select" style={{ width: 'auto', minWidth: 180 }} value={event} onChange={(e) => setEvent(e.target.value)}>
            <option value="all">All events</option>
            {events.map((e) => (
              <option key={e}>{e}</option>
            ))}
          </select>
          <label className="lbl u-faint" style={{ fontSize: 'var(--fs-sm)' }} htmlFor="pos-strategy">
            Strategy
          </label>
          <select id="pos-strategy" className="select" style={{ width: 'auto', minWidth: 160 }} value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <option value="all">All strategies</option>
            {strategies.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <span className="spacer" />
          <span className="cell-sub">Sort</span>
          <ButtonGroup
            label="Sort positions"
            value={sort}
            onChange={setSort}
            options={[
              { value: 'liability', label: 'Liability' },
              { value: 'kickoff', label: 'Kick-off' },
            ]}
          />
        </CardBody>
      </Card>

      {(data?.accumulators.length ?? 0) > 0 && <Accumulators bets={data!.accumulators} />}

      <Card style={{ marginBottom: 16 }}>
        <CardHead
          title="Positions"
          sub="Outcome P&L is shown per runner — read every row, it is a vector not a single number"
        />
        <CardBody flush>
          {loading ? (
            <div style={{ padding: 18 }}>
              <Skeleton rows={6} />
            </div>
          ) : rows.length === 0 ? (
            <StateBlock
              title={
                data?.positions.length
                  ? 'Nothing matches this filter'
                  : data?.accumulators.length
                    ? 'No singles open'
                    : 'Nothing open right now'
              }
              text={
                data?.positions.length
                  ? 'No position matches the filters above. Clear them to see the whole book.'
                  : 'Singles you place show here, per market, with their liability and outcome P&L. Add prices to your bet slip from any game.'
              }
              action={
                <Link className="btn primary" to="/">
                  Browse games
                </Link>
              }
            />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Market &amp; event</th>
                    <th>State</th>
                    <th className="num">Liability</th>
                    <th>Outcome P&amp;L per runner</th>
                    <th className="num">Orders</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <PositionTableRow key={p.marketId} position={p} />
                  ))}
                </tbody>
                {t && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border-2)', background: 'var(--surface-2)' }}>
                      <td colSpan={2}>
                        <span className="u-bold">Portfolio total</span>
                        <div className="cell-sub">Total liability</div>
                      </td>
                      <td className="num">
                        <span className="u-bold">{gbp(t.liability)}</span>
                      </td>
                      <td className="cell-sub">
                        {t.worstSingleOutcome ? (
                          <>
                            Worst-case single result: <span className="neg u-mono">{pnl(t.worstSingleOutcome.pnl)}</span> (
                            {t.worstSingleOutcome.label})
                          </>
                        ) : (
                          'No losing outcome in the book'
                        )}
                      </td>
                      <td className="num">
                        {t.matchedFills} <span className="cell-sub">/ {t.workingOrders}</span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardBody>
        <CardFoot>
          A bet stands until its game is settled; there is no cashing out.
        </CardFoot>
      </Card>
    </main>
  )
}

/** Open accumulators: one stake on every leg, lost if any leg loses. */
function Accumulators({ bets }: { bets: PnlBet[] }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <CardHead title="Accumulators" sub="One stake on every leg — it wins only if they all do" />
      <CardBody flush>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Legs</th>
                <th className="num">Odds</th>
                <th className="num">Stake</th>
                <th className="num">Returns</th>
              </tr>
            </thead>
            <tbody>
              {bets.map((b) => (
                <tr key={b.ref}>
                  <td>
                    <div className="cell-strong" style={{ marginBottom: 4 }}>
                      {b.legs.length}-fold accumulator
                    </div>
                    <ul className="acc-legs">
                      {b.legs.map((l, i) => (
                        <li key={i}>
                          <Link to={`/markets/${l.marketId}`}>{l.runner}</Link>{' '}
                          <span className="u-mono">@ {odds(l.price)}</span>{' '}
                          <span className="cell-sub">{[l.event, l.marketType].filter(Boolean).join(' · ')}</span>{' '}
                          {l.result === 'won' ? (
                            <Badge tone="pos">Won</Badge>
                          ) : l.result === 'void' ? (
                            <Badge tone="skipped">Void</Badge>
                          ) : (
                            <Badge dot>Open</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="num u-mono">{odds(b.price)}</td>
                  <td className="num u-mono u-bold">{gbp(b.stake)}</td>
                  <td className="num u-mono pos">{gbp(b.stake * b.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  )
}

function PositionTableRow({ position: p }: { position: PositionRow }) {
  const kick = toKickOff(p.startTime)

  return (
    <tr>
      <td>
        <div className="cell-strong">{p.event}</div>
        <div className="cell-sub">
          {p.marketType} · {p.strategy}
        </div>
      </td>
      <td>
        {p.settling ? (
          <Badge tone="info" dot>
            Settling
          </Badge>
        ) : p.suspended ? (
          <Badge tone="warn" dot>
            Suspended
          </Badge>
        ) : p.inPlay ? (
          <Badge tone="warn" dot>
            In-play {p.minute ? `${p.minute}'` : ''}
          </Badge>
        ) : (
          <Badge dot>Pre-match · {kick.label}</Badge>
        )}
        {p.score && <div className="cell-sub" style={{ marginTop: 3 }}>Score {p.score}</div>}
        {p.suspended && (
          <div className="cell-sub" style={{ marginTop: 3 }}>
            Betting off while prices re-form — liability still stands
          </div>
        )}
      </td>
      <td className="num">
        <span className="u-bold">{gbp(p.liability)}</span>
      </td>
      <td>
        <OutcomePnl outcomes={p.outcomes} style={{ minWidth: 210 }} />
      </td>
      <td className="num">
        {p.matchedOrders} <span className="cell-sub">/ {p.workingOrders}</span>
      </td>
      <td>
        <div className="row" style={{ gap: 6 }}>
          <Link className="btn sm" to={`/markets/${p.marketId}`}>
            Open
          </Link>
          {p.unmatchedSize > 0 && (
            <span className="badge warn" title="Unmatched size still working on the exchange">
              {gbp(p.unmatchedSize)} working
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}

/**
 * Exposure we did not open means every other limit is computed from an incomplete
 * picture — so trading stops until it is resolved. This is a blocking state, not a note.
 */
function OrphanBanner({ orphan }: { orphan: NonNullable<PositionsPayload['orphan']> }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <CardHead title="Position opened outside KellyStake" tag={<StateTag tone="warn">Orphan</StateTag>} />
      <CardBody>
        <Banner tone="degraded" title="Orphan position detected — trading is blocked until this is resolved">
          Reconciliation found a bet on your exchange account that KellyStake did not place:{' '}
          <b>
            {orphan.side} {orphan.runner} @ {orphan.price} · {gbp(orphan.size)}
          </b>{' '}
          on <b>{orphan.event} · {orphan.marketType}</b>. Because we cannot attribute it to a strategy or a risk
          decision, we cannot guarantee its liability sits inside your limits. All new orders — automated and manual —
          are paused while it exists.
        </Banner>
        <KV style={{ marginBottom: 14 }}>
          <KVRow label="Selection" text>
            <SideBadge side={orphan.side} /> {orphan.runner} @ {orphan.price}
          </KVRow>
          <KVRow label="Matched size">{gbp(orphan.size)}</KVRow>
          <KVRow label="Implied liability">
            <span className="neg">{gbp(orphan.liability)}</span>
          </KVRow>
          <KVRow label="Attributed strategy" text>
            <span className="muted">none — placed off-platform</span>
          </KVRow>
        </KV>
        <div className="row" style={{ gap: 8 }}>
          <Link className="btn primary" to="/reconciliation">
            Resolve in reconciliation
          </Link>
        </div>
      </CardBody>
    </Card>
  )
}
